import { useCallback, useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { TaskImage } from '@shared/domain.ts';
import { cn } from '@/lib/format.ts';
import { useAttachments } from '@/lib/uploads.ts';
import { AttachButton, AttachmentPills } from './Attachments.tsx';
import { Button, Textarea, useToast } from './ui.tsx';
import { Shot } from './Shot.tsx';

/**
 * A screenshot, full size, with the rest of the set a keypress away.
 *
 * Screenshots are how somebody decides whether a routine built the right thing,
 * and that decision is usually "this one is wrong, here is why". Opening each in
 * a new tab to look and then coming back to type breaks the one thing the panel
 * is for. So the comment box lives under the image: look, say what is wrong,
 * send, keep going.
 *
 * Not a general-purpose gallery. It knows about a task because the comment has
 * to reach one.
 */
export function Lightbox({
  images,
  index,
  onIndex,
  onClose,
  onComment,
  projectId,
}: {
  images: TaskImage[];
  index: number;
  onIndex: (index: number) => void;
  onClose: () => void;
  onComment: (body: string) => Promise<unknown>;
  projectId: string | undefined;
}) {
  const { notify } = useToast();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);

  const {
    items: attachments,
    onDrop,
    onDragOver,
    onPaste,
    attach,
    remove: removeAttachment,
    clear: clearAttachments,
    compose,
    uploading,
    hasAttachment,
  } = useAttachments({ projectId, notify });

  const image = images[index];
  const count = images.length;

  // Wrap rather than stop: with a handful of screenshots, running off the end
  // and having to turn around is more annoying than losing your place.
  const step = useCallback(
    (delta: number) => onIndex((index + delta + count) % count),
    [index, count, onIndex],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // Not while typing — arrows move the caret, and a comment being written
      // is the more important thing in the room.
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT')) return;
      if (event.key === 'ArrowRight') { event.preventDefault(); step(1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); step(-1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step]);

  if (!image) return null;

  const submit = async () => {
    const text = compose(body.trim());
    if (!text) return;
    setBusy(true);
    try {
      // Which screenshot it is about, since the comment lands in a thread where
      // that is otherwise unrecoverable.
      const about = image.label ? `**${image.label}**` : `**Screenshot ${index + 1}**`;
      await onComment(`${about}\n\n${text}`);
      setBody('');
      clearAttachments();
      notify('Comment posted');
    } catch (error) {
      notify((error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogPrimitive.Root open onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-[#0b0e18]/85 backdrop-blur-[3px]" />
        {/*
          Radix closes on a click outside the content, but the content here *is*
          the whole screen — there is no outside to click. So the backdrop is
          this element, and the two panels stop the click travelling.
        */}
        <DialogPrimitive.Content
          className="rg-anim fixed inset-0 z-50 flex cursor-zoom-out flex-col items-center justify-center gap-3 p-4 outline-none sm:p-8"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onClick={onClose}
        >
          <DialogPrimitive.Title className="sr-only">
            {image.label ?? `Screenshot ${index + 1} of ${count}`}
          </DialogPrimitive.Title>

          <div
            className="flex w-full max-w-5xl cursor-default items-center gap-1.5 sm:gap-3"
            onClick={(event) => event.stopPropagation()}
          >
            {count > 1 ? <Arrow direction="left" onClick={() => step(-1)} /> : null}

            <figure className="m-0 min-w-0 flex-1">
              {/*
                Below `sm`, a screenshot is usually a desktop-sized image on a
                phone-sized screen — shrinking it to fit is exactly what makes
                it unreadable. So on mobile this wraps in a scroll box and the
                image renders at its natural pixel size (`max-w-none` beats the
                shrink-to-fit default): pinch-zoom and panning read it at 100%
                instead of a downscaled blur. `sm:contents` drops the wrapper
                above that breakpoint, where there is room to just fit the
                whole image and scrolling would be a step backwards.
              */}
              <div className="max-h-[65vh] overflow-auto rounded-md sm:contents">
                <Shot
                  url={image.url}
                  label={image.label}
                  variant="dark"
                  className="max-w-none sm:max-h-[62vh] sm:w-full sm:max-w-full sm:!object-contain"
                />
              </div>
              <figcaption className="mt-2 flex items-baseline gap-2 text-[12.5px] text-soft">
                <span className="truncate">{image.label ?? 'Screenshot'}</span>
                {count > 1 ? (
                  <span className="ml-auto shrink-0 font-mono text-[11.5px] text-faint">
                    {index + 1}/{count}
                  </span>
                ) : null}
                <a
                  href={image.url}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 text-[11.5px]"
                >
                  open original
                </a>
              </figcaption>
            </figure>

            {count > 1 ? <Arrow direction="right" onClick={() => step(1)} /> : null}
          </div>

          <div
            className="w-full max-w-5xl cursor-default rounded-xl border border-line bg-aside p-2.5 sm:p-3"
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className="relative"
              onDragEnter={(event) => {
                if (event.dataTransfer.types.includes('Files')) setDragging(true);
              }}
              onDragOver={onDragOver}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false);
              }}
              onDrop={(event) => {
                setDragging(false);
                onDrop(event);
              }}
            >
              <Textarea
                ref={box}
                value={body}
                onChange={(event) => setBody(event.target.value)}
                onKeyDown={(event) => {
                  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                    event.preventDefault();
                    void submit();
                  }
                }}
                onPaste={onPaste}
                placeholder={`Comment on ${image.label ?? 'this screenshot'}…`}
                className="min-h-14"
                aria-label="Comment on this screenshot"
              />
              {dragging ? (
                <div className="rg-anim pointer-events-none absolute inset-0 grid place-items-center rounded-[10px] border-2 border-dashed border-accent bg-app/90 text-[13px] font-semibold text-accent">
                  Drop to attach
                </div>
              ) : null}
            </div>
            <AttachmentPills items={attachments} onRemove={removeAttachment} />
            <div className="mt-2 flex items-center gap-2.5 sm:gap-3">
              <AttachButton onFiles={attach} />
              {/* Explains itself; the phone's width goes to the Comment button instead. */}
              <span className="hidden text-[12px] text-mute sm:inline">
                The screenshot's name goes with it, so the routine knows which one you mean.
              </span>
              <span className="flex-1" />
              <kbd className="hidden text-[11px] text-faint sm:inline">⌘↩</kbd>
              <Button
                tone="primary"
                size="sm"
                disabled={busy || uploading || (!body.trim() && !hasAttachment)}
                onClick={() => void submit()}
              >
                {uploading ? 'Uploading…' : 'Comment'}
              </Button>
            </div>
          </div>

          <DialogPrimitive.Close asChild>
            <Button size="sm" className="absolute top-4 right-4">
              Close
            </Button>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function Arrow({ direction, onClick }: { direction: 'left' | 'right'; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={direction === 'left' ? 'Previous screenshot' : 'Next screenshot'}
      className={cn(
        'rg-focus grid size-8 shrink-0 cursor-pointer place-items-center rounded-full sm:size-9',
        'border border-line bg-aside text-t2 hover:bg-btnhover hover:text-hi2',
      )}
    >
      <svg viewBox="0 0 16 16" aria-hidden className="size-4 fill-current">
        <path
          d={
            direction === 'left'
              ? 'M10.3 2.3 4.6 8l5.7 5.7 1.1-1.1L6.8 8l4.6-4.6z'
              : 'M5.7 2.3 4.6 3.4 9.2 8l-4.6 4.6 1.1 1.1L11.4 8z'
          }
        />
      </svg>
    </button>
  );
}
